import React from 'react';
import { Paperclip } from 'lucide-react';
import { FormComponentType as TFormComponent } from '../df-form-controls/df-form-preview-interfaces';
import './ComponentSubmissionActions.scss';

interface IComponentSubmissionActionsProps {
  component: TFormComponent;
}

interface IAttachmentData {
  name: string;
  type: string;
  size: number;
  data: string;
}

// Attachment Thumbnails Component for Submission View
const SubmissionAttachmentThumbnails: React.FC<{
  attachments: IAttachmentData[];
}> = ({ attachments }) => {
  // Convert base64 data to data URI for display
  const getImageSrc = (attachment: IAttachmentData): string => {
    if (!attachment.data) return '';
    
    // If already a data URI, return as is
    if (attachment.data.startsWith('data:')) {
      return attachment.data;
    }
    
    // Convert base64 to data URI
    const mimeType = attachment.type || 'image/png';
    return `data:${mimeType};base64,${attachment.data}`;
  };

  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div className="attachment-thumbnails-container">
      {attachments.map((attachment, index) => {
        const isImage = attachment.type?.startsWith('image/') || false;
        const imageSrc = isImage ? getImageSrc(attachment) : '';
        
        return (
          <div key={index} className="attachment-thumbnail">
            {isImage && imageSrc ? (
              <img src={imageSrc} alt={attachment.name || `Attachment ${index + 1}`} />
            ) : (
              <div className="file-icon-placeholder">
                <Paperclip size={20} />
                <span className="file-name">{attachment.name || `File ${index + 1}`}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const ComponentSubmissionActions: React.FC<IComponentSubmissionActionsProps> = ({
  component
}) => {
  // Extract notes and attachments from component.basic
  const notes = (component as any).basic?.notes || '';
  const attachments = ((component as any).basic?.attachments || []) as IAttachmentData[];

  const hasNotes = notes && notes.trim().length > 0;
  const hasAttachments = attachments && attachments.length > 0;

  // Don't render if there are no notes or attachments
  if (!hasNotes && !hasAttachments) {
    return null;
  }

  return (
    <div className="component-submission-actions">
      {/* Notes and Attachments Content - Always visible */}
      <div className="actions-content">
        {/* Notes Summary */}
        {hasNotes && (
          <div className="notes-summary-section">
            <div className="notes-full-text">
              <span className="notes-label">Notes:</span> {notes}
            </div>
          </div>
        )}

        {/* Attachment Thumbnails */}
        {hasAttachments && (
          <div className="attachments-section">
            <SubmissionAttachmentThumbnails attachments={attachments} />
          </div>
        )}
      </div>
    </div>
  );
};

